import React, { useState, useContext } from "react"
import PropTypes from "prop-types"
import { ThemeContext } from "styled-components"

import Box from "../atoms/Box"
import Flex from "../atoms/Flex"
import Icon from "../atoms/Icon"
import NavLink from "../atoms/NavLink"
import { Label, Text } from "../atoms/Typography"
import Caret from "../icons/Caret"
import Button from "../atoms/Button"
import RichText from "../atoms/RichText"

const NavMegaMenuMobile = ({
  item,
  onDismiss,
  mt,
  linkAlignmentOnMobile = "left",
}) => {
  const theme = useContext(ThemeContext)
  const variant = "dropdownLink"
  const isIconTop = item.iconLocation === "top"
  const navLinkTypography = theme?.typography?.[variant]

  const [isOpen, setIsOpen] = useState(false)
  const [isCaretHovered, setIsCaretHovered] = useState(false)

  // color states for caret
  const defaultCaretColor =
    navLinkTypography?.color || theme.colors.text || "inherit"
  const caretHoverColor =
    navLinkTypography?.["& :hover"]?.color || defaultCaretColor
  const caretActiveColor =
    navLinkTypography?.["& :active"]?.color || defaultCaretColor

  // logic for caret color
  let currentCaretColor = defaultCaretColor
  if (isOpen && isCaretHovered) {
    currentCaretColor = caretHoverColor
  } else if (isOpen) {
    currentCaretColor = caretActiveColor
  } else if (isCaretHovered) {
    currentCaretColor = caretHoverColor
  }

  const handleToggle = () => setIsOpen(!isOpen)
  const handleLinkClick = () => {
    if (onDismiss) onDismiss()
  }

  const renderColumns = () => {
    if (!item.megaMenuColumns?.length) return null

    return item.megaMenuColumns.map((col, idxCol) => {
      return (
        <Box key={col.strapi_id || idxCol} width="100%" mt={2} textAlign="left">
          {/* Column Title */}
          {col.linkTitle && (
            <Label
              variant="MegaMenuColumnTitle"
              display="inline-block"
              mt={idxCol === 0 ? 3 : 0}
              mb={1}
            >
              {col.linkTitle}
            </Label>
          )}

          {/* Each link of this column */}
          {col.megaMenuLinks?.map((megaLink, idxLink2) => {
            const linkDesc = item.showDescription
              ? megaLink.descriptionRichText
              : null
            const hasIcon = item.showIcon && megaLink?.iconAttribute?.image?.url

            const flexDirection = isIconTop ? "column" : "row"

            return (
              <Box key={megaLink.strapi_id || idxLink2} mt={3}>
                {/* Icon on the left if iconLocation='left' and on top if iconLocation==='center' */}
                <Flex
                  flexDirection={flexDirection}
                  alignItems="flex-start"
                  justifyContent="flex-start"
                >
                  {hasIcon && (
                    <Box
                      width={`${item.iconSize || 36}px`}
                      height={`${item.iconSize || 36}px`}
                      mr={isIconTop ? 0 : 3}
                      mb={isIconTop ? 1 : 0}
                      flexShrink={0}
                    >
                      <Icon
                        url={megaLink.iconAttribute.image.url}
                        alternativeText={
                          megaLink.iconAttribute.image.alternativeText || ""
                        }
                        width={`${item.iconSize || 36}px`}
                        height={`${item.iconSize || 36}px`}
                      />
                    </Box>
                  )}

                  <Box display="flex" flexDirection="column" alignItems="start">
                    {/* Link Title  */}
                    {megaLink.link ? (
                      <NavLink
                        aria-label="Mobile mega link"
                        variant="MegaMenuLinkTitle"
                        linkText={megaLink.link.linkText || megaLink.name}
                        {...megaLink.link}
                        onClick={handleLinkClick}
                      />
                    ) : (
                      <Text fontWeight="bold" fontSize={2}>
                        {megaLink.name}
                      </Text>
                    )}

                    {/* Link Description */}
                    {linkDesc && (
                      <Box>
                        <RichText
                          mt={1}
                          mb={-4}
                          textAlign="left"
                          variant="MegaMenuDescription"
                          {...linkDesc}
                        />
                      </Box>
                    )}

                    {/* Button */}
                    {megaLink.button?.text && (
                      <Box mt={2} mb={5}>
                        <Button
                          aria-label="Mobile mega link button"
                          {...megaLink.button}
                          onClick={handleLinkClick}
                        />
                      </Box>
                    )}
                  </Box>
                </Flex>
              </Box>
            )
          })}
        </Box>
      )
    })
  }

  return (
    <Box mt={mt} width="100%">
      {/* dropdown menu (label + caret) */}
      <Flex
        alignItems="center"
        justifyContent={
          linkAlignmentOnMobile === "left" ? "flex-start" : "center"
        }
        onMouseEnter={() => setIsCaretHovered(true)}
        onMouseLeave={() => setIsCaretHovered(false)}
        onClick={handleToggle}
        style={{ cursor: "pointer" }}
      >
        <Text variant={variant} shouldInheritColor={false} fontSize={3} mr={2}>
          {item.linkTitle || "Mega Menu"}
          <Caret
            direction={isOpen ? "up" : "down"}
            width="16px"
            height="16px"
            color={currentCaretColor}
          />
        </Text>
      </Flex>

      {/* show columns */}
      {isOpen && <Box mt={3}>{renderColumns()}</Box>}
    </Box>
  )
}

NavMegaMenuMobile.propTypes = {
  item: PropTypes.object.isRequired,
  onDismiss: PropTypes.func,
  mt: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  linkAlignmentOnMobile: PropTypes.oneOf(["left", "center"]),
}

export default NavMegaMenuMobile
